import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from "@/config/emailjs";
import { supabase } from "@/integrations/supabase/client";
import { emailLogger } from '@/utils/logger';

export interface EmailData {
  name: string;
  email: string;
  phone: string;
  eventDate: string;
  products: {
    sumo: boolean;
    chateau: boolean;
  };
  chateauType?: string;
  message?: string;
}

export interface EmailResult {
  success: boolean;
  method: 'emailjs' | 'supabase' | 'none';
  error?: string;
}

/**
 * Service for handling email operations with fallback mechanisms
 */
export class EmailService {
  /**
   * Get display name for château type
   */
  private static getChateauDisplayName(id: string, chateaux?: any[]): string {
    if (chateaux) {
      const dbChateau = chateaux.find(c => c.id === id);
      if (dbChateau) {
        return dbChateau.nom;
      }
    }
    
    const names: { [key: string]: string } = {
      "disco-m": "Château Disco M",
      "grenouille": "Château Grenouille", 
      "disco-lm": "Château Disco LM",
      "twister": "Château Twister",
      "dino": "Château Dino",
      "vache": "Château Vache",
      "princesse": "Château Princesse",
      "pingouin": "Château Pingouin",
      "Standard": "Château Standard",
      "Tapis": "Tapis Gonflable",
      "sumos": "Sumos Gonflables"
    };
    return names[id] || id.charAt(0).toUpperCase() + id.slice(1);
  }

  /**
   * Send email via EmailJS
   */
  static async sendViaEmailJS(data: EmailData, chateaux?: any[]): Promise<EmailResult> {
    emailLogger.info("Début envoi via EmailJS");
    emailLogger.debug("Configuration EmailJS", EMAILJS_CONFIG);
    
    try {
      const produits = [];
      if (data.products.sumo) produits.push("Sumo (50€)");
      if (data.products.chateau) {
        const chateauName = data.chateauType ? this.getChateauDisplayName(data.chateauType, chateaux) : "Château Gonflable";
        produits.push(chateauName);
      }

      // Calcul du prix total et de la caution
      // Caution : 50€ par défaut, 150€ pour les châteaux "disco"
      let prixTotal = 0;
      let cautionTotal = 0;
      if (data.products.sumo) {
        prixTotal += 50;
        cautionTotal += 50;
      }
      if (data.products.chateau && data.chateauType) {
        const selectedChateau = chateaux?.find(c => c.id === data.chateauType);
        if (selectedChateau?.prix) {
          const prixNum = parseInt(String(selectedChateau.prix).replace(/[^0-9]/g, ''), 10);
          if (!isNaN(prixNum)) prixTotal += prixNum;
        }
        const isDisco = /disco/i.test(data.chateauType) || /disco/i.test(selectedChateau?.nom || '');
        cautionTotal += isDisco ? 150 : 50;
      }

      const emailParams = {
        user_name: data.name,
        user_email: data.email,
        user_phone: data.phone,
        event_date: new Date(data.eventDate).toLocaleDateString('fr-FR'),
        selected_products: produits.join(', '),
        chateau_type: data.chateauType ? this.getChateauDisplayName(data.chateauType, chateaux) : '',
        message: data.message || 'Aucun message spécial',
        PRIX: prixTotal > 0 ? `${prixTotal}€` : 'À définir',
        CAUTION: cautionTotal > 0 ? `${cautionTotal}€` : 'À définir'
      };

      emailLogger.debug("Paramètres email", emailParams);

      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        emailParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      emailLogger.info("Email envoyé via EmailJS", result);
      return { success: true, method: 'emailjs' };

    } catch (error: any) {
      emailLogger.error("Erreur EmailJS", error);
      return { success: false, method: 'emailjs', error: error.message };
    }
  }

  /**
   * Send email via Supabase Edge Function (fallback)
   */
  static async sendViaSupabase(data: EmailData): Promise<EmailResult> {
    emailLogger.info("Tentative envoi via Supabase Edge Function");
    
    try {
      const { data: result, error } = await supabase.functions.invoke('send-reservation-email', {
        body: data,
      });

      if (error) {
        emailLogger.error("Erreur Supabase", error);
        return { success: false, method: 'supabase', error: error.message };
      }

      if (!result?.success) {
        emailLogger.error("Erreur serveur", result?.error);
        return { success: false, method: 'supabase', error: result?.error || "Erreur serveur inconnue" };
      }

      emailLogger.info("Email envoyé via Supabase");
      return { success: true, method: 'supabase' };

    } catch (error: any) {
      emailLogger.error("Erreur Supabase", error);
      return { success: false, method: 'supabase', error: error.message };
    }
  }

  /**
   * Send error notification to admin
   */
  static async sendErrorNotification(data: EmailData, errorMessage: string, chateaux?: any[]): Promise<void> {
    emailLogger.warn("Envoi notification d'erreur");
    
    try {
      const produits = [];
      if (data.products.sumo) produits.push("Sumo (50€)");
      if (data.products.chateau) {
        const chateauName = data.chateauType ? this.getChateauDisplayName(data.chateauType, chateaux) : "Château Gonflable";
        produits.push(chateauName);
      }

      const errorParams = {
        user_name: "Château de Clémentine",
        user_email: "lechateaudeclementine@gmail.com",
        user_phone: "Erreur système",
        event_date: new Date().toLocaleDateString('fr-FR'),
        selected_products: "🚨 ERREUR FORMULAIRE - Voir message",
        chateau_type: `Client: ${data.name}`,
        message: `🚨 ERREUR D'ENVOI DE FORMULAIRE

Client: ${data.name}
Email: ${data.email}
Téléphone: ${data.phone}
Date événement: ${new Date(data.eventDate).toLocaleDateString('fr-FR')}
Produits: ${produits.join(', ')}

Erreur technique: ${errorMessage}

⚠️ VEUILLEZ CONTACTER CE CLIENT MANUELLEMENT
Email: ${data.email}
Téléphone: ${data.phone}

Timestamp: ${new Date().toISOString()}`
      };

      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        errorParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      emailLogger.info("Notification d'erreur envoyée");
    } catch (emailError) {
      emailLogger.error("Impossible d'envoyer notification d'erreur", emailError);
    }
  }

  /**
   * Main email sending method with fallback chain
   */
  static async sendReservationEmail(data: EmailData, chateaux?: any[]): Promise<EmailResult> {
    emailLogger.info("Début envoi email de réservation");

    // Try EmailJS first
    const emailjsResult = await this.sendViaEmailJS(data, chateaux);
    if (emailjsResult.success) {
      return emailjsResult;
    }

    // Fallback to Supabase
    emailLogger.warn("EmailJS échoué, passage au fallback Supabase");
    const supabaseResult = await this.sendViaSupabase(data);
    if (supabaseResult.success) {
      return supabaseResult;
    }

    // Both failed - send error notification
    const combinedError = `EmailJS: ${emailjsResult.error} | Supabase: ${supabaseResult.error}`;
    await this.sendErrorNotification(data, combinedError, chateaux);

    return { 
      success: false, 
      method: 'none', 
      error: combinedError 
    };
  }
}